import { Swiper, Navigation, Controller } from 'swiper';
import gsap from 'gsap';

export default HeroMain = () => {
    const sections = document.querySelectorAll('.hero-main');

    Swiper.use([ Controller, Navigation ]);

    let slidingViaNavigation = null;

    for (const section of sections) {
        const swiperEl = section.querySelector('.swiper.swiper-container');
        const mobileSwiperEl = section.querySelector('.swiper.mobile-swiper-container');
        const circleDecoration = section.querySelector('.circle-decoration');

        if (!swiperEl) continue;

        const slideDuration = (parseInt(swiperEl.dataset.swiperTransitionDuration) / 1000) || .45;
        const delay = parseInt(swiperEl.dataset.swiperSpeed, 10) || 2000;

        let numSlides = 0;
        let lastProgress = 0;
        let lastSlideProgress = 0;
        let hasInteracted = false;
        let autoplayInterval = null;
        let curDuration = 0;
        setTimeout(() => {
            curDuration = slideDuration;
        }, 1000);

        const clamp = (num, min = 0, max = 100) => Math.min(Math.max(num, min), max);

        const getAnimPoints = (progress) => {
            const p1 = progress > 50 ? clamp((progress - 100 * (2 / 3)) * 3) : clamp(100 - (progress * 3));
            const p2 = clamp(100 - ((progress - 100 * (1 / 3)) * 3));
            const p3 = clamp((progress - 100 * (2 / 3)) * 3);
            const p4 = clamp(100 - ((progress - 100 * (1 / 3)) * 3));
            const p5 = clamp((progress - 100 * (2 / 3)) * 3);

            return {
                p1,
                p2,
                p3,
                p4,
                p5,
            }
        }

        const getDurationFactors = (el) => {
            const rect = el.getBoundingClientRect();
            const total = rect.width + rect.height * 2;
            const x = rect.width / total;
            const y = rect.height / total;
        
            return {
                x,
                y,
            }
        }

        const initAutoplay = (swiper) => {
            swiper.slides.forEach((slide, index) => {
                slide.style.opacity = 1;
            });
            if (swiper.el.hasAttribute('data-swiper-autoplay')) {
                autoplayInterval = setInterval(() => {
                    if (hasInteracted) {
                        clearInterval(autoplayInterval);
                        return;
                    }
                    swiper.slideToLoop(swiper.realIndex + 1);
                }, delay);
            }
        };

        const createSwiper = (element) => {
            let isFirstChange = true;
            let circleStartX = 0;
            let factors = getDurationFactors(swiperEl);

            window.addEventListener('resize', () => {
                factors = getDurationFactors(swiperEl);
            })

            const _setTranslate = (swiper, wrapperTranslate) => {
                const numSlides = swiper.slides.length - 1;
                
                // This solution won't work if users can jump to slides
                const singleSlideDistance = 1 / numSlides
                const isLooping = Math.abs(swiper.progress - lastProgress) > singleSlideDistance;

                let currentSlide = swiper.progress * numSlides;
                let currentPercentage = (currentSlide % 1) * 100;
                let direction = 'NONE';


                if (slidingViaNavigation) {
                        direction = slidingViaNavigation;
                } else {
                    if (swiper.progress > lastProgress && !isLooping) {
                        direction = 'NEXT';
                        // Prevents glitchy image animation if user changes direction while dragging
                        if (swiper.swipeDirection === 'prev') {
                            direction = 'BACK';
                        }
                    } else if (swiper.progress < lastProgress && !isLooping) {
                        direction = 'BACK';
                        // Prevents glitchy image animation if user changes direction while dragging
                        if (swiper.swipeDirection === 'next') {
                            direction = 'NEXT';
                        }
                    }
                }

                const applyClipPathAnimation = (tl, slide, clipPath, duration, ease = 'none') => 
                    tl.to(slide, { duration, clipPath, ease });

                const getSlideClipPath = (points) => 
                    `polygon(0% ${points.p5}%, ${points.p4}% ${points.p3}%, ${points.p2}% ${points.p1}%, 50% 100%, 0% 100%)`;

                const setSlideOpacityPointerEvents = (slide, opacity, pointerEvents) => 
                    gsap.set(slide, { opacity, pointerEvents });

                const setSlideVisibility = (slide, opacity, pointerEvents, clipPath = null) => 
                    gsap.set(slide, { opacity, pointerEvents, ...(clipPath && { clipPath }) }); // Sets clip-path only if it's provided

                swiper.slides.forEach((slide, index) => {
                    
                    if (direction === 'NEXT') {
                        const image = slide.querySelector('.image-main');
                        const tl = gsap.timeline();
                        const finalClipPath = `polygon(0% 100%, 0% 100%, 0% 100%, 50% 100%, 0% 100%)`;

                        // Slide being swiped away
                        if (index === swiper.progress * numSlides - 1) {
                            setSlideOpacityPointerEvents(slide, 1, 'all');

                            if (lastSlideProgress > (2 / 3) * 100) {
                                const points = getAnimPoints(lastSlideProgress);
                                tl.set(slide, { clipPath: getSlideClipPath(points) });
                                applyClipPathAnimation(tl, slide, finalClipPath, curDuration * factors.y * ((100 - lastSlideProgress) * 3) / 100);
                                tl.set(slide, { opacity: 0, pointerEvents: 'none' });

                            } else if (lastSlideProgress > (1 / 3) * 100) {
                                const points = getAnimPoints(lastSlideProgress);
                                tl.set(slide, { clipPath: getSlideClipPath(points) });
                                applyClipPathAnimation(tl, slide, `polygon(0% 0%, 0% 0, 0% 0%, 50% 100%, 0% 100%)`, curDuration * factors.x * ((100 - (lastSlideProgress - (100 * (1 / 3))) * 3) / 100));
                                applyClipPathAnimation(tl, slide, finalClipPath, curDuration * factors.y);
                                tl.set(slide, { opacity: 0, pointerEvents: 'none' });

                            } else {
                                const points = getAnimPoints(lastSlideProgress);
                                tl.set(slide, { clipPath: getSlideClipPath(points) });
                                applyClipPathAnimation(tl, slide, `polygon(0% 0%, 100% 0, 100% 0%, 50% 100%, 0% 100%)`, curDuration * factors.y * ((100 - (lastSlideProgress * 3)) / 100));
                                applyClipPathAnimation(tl, slide, `polygon(0% 0%, 0% 0, 0% 0%, 50% 100%, 0% 100%)`, curDuration * factors.x);
                                applyClipPathAnimation(tl, slide, finalClipPath, curDuration * factors.y);
                                tl.set(slide, { opacity: 0, pointerEvents: 'none' });
                            }

                            gsap.set(image, { rotate: 0 });

                        // Slide that’s next in line
                        } else if (index === Math.ceil(swiper.progress * numSlides - 1)) {
                            const points = getAnimPoints(currentPercentage);
                            tl.set(slide, {
                                opacity: 1,
                                pointerEvents: 'all',
                                clipPath: getSlideClipPath(points),
                            });
                            gsap.set(image, { rotate: 0 });

                        // Target slide being swiped to
                        } else if (index === swiper.progress * numSlides) {
                            const startClipPath = `polygon(0% 0%, 100% 0%, 100% 100%, 50% 100%, 0% 100%)`;
                            setSlideOpacityPointerEvents(slide, 1, 'all');
                            gsap.set(slide, { clipPath: startClipPath });
                            gsap.fromTo(image, { rotate: 10 }, { rotate: 0, duration: curDuration });

                        // Slide that is before the current slide
                        } else if (index < swiper.progress * numSlides - 1) {
                            setSlideOpacityPointerEvents(slide, 0, 'none');
                            gsap.set(image, { rotate: -10 });

                        // Slide that is after the current slide
                        } else {
                            const startClipPath = `polygon(0% 0%, 100% 0%, 100% 100%, 50% 100%, 0% 100%)`;
                            gsap.set(slide, { opacity: 1, clipPath: startClipPath });
                            gsap.set(image, { rotate: 10 });
                        }
                    } else if (direction === 'BACK') {
                        const image = slide.querySelector('.image-main');
                        const tl = gsap.timeline();

                        // The slide being swiped in
                        if (index === swiper.progress * numSlides) {
                            const points = getAnimPoints(lastSlideProgress);

                            if (slidingViaNavigation === 'BACK') {
                                tl.set(slide, { clipPath: `polygon(0% 100%, 0% 100%, 0% 100%, 50% 100%, 0% 100%)` });
                                applyClipPathAnimation(tl, slide, `polygon(0% 0, 0% 0%, 0% 0%, 50% 100%, 0% 100%)`, curDuration * factors.y);
                                applyClipPathAnimation(tl, slide, `polygon(0% 0%, 100% 0%, 100% 0%, 50% 100%, 0% 100%)`, curDuration * factors.x);
                                applyClipPathAnimation(tl, slide, `polygon(0% 0%, 100% 0%, 100% 100%, 50% 100%, 0% 100%)`, curDuration * factors.y);
                            } else {
                                tl.set(slide, { clipPath: getSlideClipPath(points) });
                                if (lastSlideProgress > (2 / 3) * 100) {
                                    applyClipPathAnimation(tl, slide, `polygon(0% 0, 0% 0%, 0% 0%, 50% 100%, 0% 100%)`, curDuration * factors.y * ((lastSlideProgress - (100 * (2 / 3))) * 3) / 100);
                                    applyClipPathAnimation(tl, slide, `polygon(0% 0%, 100% 0%, 100% 0%, 50% 100%, 0% 100%)`, curDuration * factors.x);
                                    applyClipPathAnimation(tl, slide, `polygon(0% 0%, 100% 0%, 100% 100%, 50% 100%, 0% 100%)`, curDuration * factors.y);
                                } else if (lastSlideProgress > (1 / 3) * 100) {
                                    applyClipPathAnimation(tl, slide, `polygon(0% 0%, 100% 0%, 100% 0%, 50% 100%, 0% 100%)`, curDuration * factors.x * ((lastSlideProgress - (100 * (1 / 3))) * 3) / 100);
                                    applyClipPathAnimation(tl, slide, `polygon(0% 0%, 100% 0%, 100% 100%, 50% 100%, 0% 100%)`, curDuration * factors.y);
                                    
                                } else {
                                    applyClipPathAnimation(tl, slide, `polygon(0% 0%, 100% 0%, 100% 100%, 50% 100%, 0% 100%)`, curDuration * factors.y * (lastSlideProgress * 3) / 100);
                                }
                            }

                            gsap.fromTo(image, { rotate: -10, duration: curDuration }, { rotate: 0 });

                            setSlideVisibility(slide, 1, 'all');

                        // Slide that is immediately before the current slide
                        } else if ((index === Math.floor(swiper.progress * numSlides))) {
                            const points = getAnimPoints(currentPercentage);
                            setSlideVisibility(slide, 1, 'all', getSlideClipPath(points));
                            gsap.set(image, { rotate: -10 });
                        } else if (index <= swiper.progress * numSlides - 1) {
                            setSlideVisibility(slide, 0, 'none');
                            gsap.set(image, { rotate: 0 });

                        // Slides behind the current slide
                        } else {
                            const finalClipPath = `polygon(0% 0%, 100% 0%, 100% 100%, 50% 100%, 0% 100%)`;
                            setSlideVisibility(slide, 1, 'all', finalClipPath);
                            gsap.set(image, { rotate: 0 });
                        }
                    } else {
                    }
                });

                lastProgress = swiper.progress;
                lastSlideProgress = currentPercentage;
            };

            return new Swiper(element, {
                slidesPerView: 1,
                loop: true,
                loopedSlides: numSlides,
                loopPreventsSliding: false,
                effect: 'KCHeroMainTransition',
                watchSlidesProgress: true,
                virtualTranslate: true,
                longSwipesRatio: 0,
                speed: slideDuration * 1000,
                watchOverflow: true,
                allowTouchMove: numSlides > 1 ? true : false,
                on: {
                    init: function (swiper) {
                        this.slides.forEach((slide, index) => {
                            slide.style.zIndex = this.slides.length - index;
                            // Fix for initial slide not being visible when using CSS flicker fix
                            if (index === swiper.activeIndex - 1) {
                                slide.style.clipPath = 'polygon(0% 100%, 0% 100%, 0% 100%, 50% 100%, 0% 100%)';
                            }
                            if (index === swiper.activeIndex) {
                                slide.style.clipPath = 'polygon(0% 0%, 100% 0%, 100% 100%, 50% 100%, 0% 100%)';
                            }
                        });
                    },
                    setTranslate(translate) {
                        _setTranslate(this, translate);
                    },
                    slideChangeTransitionStart: function () {
                        this.animating = false;
                    },
                    sliderFirstMove: () => {
                        hasInteracted = true;
                        clearInterval(autoplayInterval);
                    },
                    slideChange: () => {
                        if (!isFirstChange) return;
                        element.classList.add('loaded');
                        isFirstChange = false;
                    },
                    touchEnd: () => {
                        gsap.to(circleDecoration, { rotate: 0, duration: .3, yPercent: -50 });
                    },
                    touchStart: (swiper, e) => {
                        circleStartX = e.clientX;
                    },
                    touchMove: (swiper, e) => {
                        const diff = e.clientX - circleStartX;
                        gsap.to(circleDecoration, { rotate: Math.min(Math.max(diff / 30, -30), 30), duration: .3, yPercent: -50 });
                    },
                },
            });
        };

        const createMobileSwiper = (element) => {
            const swiper = new Swiper(element, {
                slidesPerView: 1,
                loop: true,
                loopedSlides: numSlides,
                longSwipesRatio: 0,
                spaceBetween: 100,
                speed: slideDuration * 1000,
                watchOverflow: true,
                allowTouchMove: false,
                on: {
                    sliderFirstMove: () => {
                        hasInteracted = true;
                        clearInterval(autoplayInterval);
                    },
                }
            })

            const update = () => {
                if (window.innerWidth >= 768) {
                    return;
                }
                swiper.slides.forEach((slide) => {        
                    const rect = slide.getBoundingClientRect();
                    if (rect.x > window.innerWidth || rect.x + rect.width < 0) return;
                    let yVal = Math.abs(windowWidthCenter - rect.x - rect.width / 2);
                    yVal = (yVal) * (yVal) * 1.3 / (window.innerWidth * 2);
    
                    rotateVal = (windowWidthCenter - rect.x - rect.width / 2) / -10;
    
                    slide.style.transform = `translateY(${yVal}px) rotate(${rotateVal}deg)`;
                })
    
                requestAnimationFrame(update);
            }
            
            if (window.innerWidth < 768) {
                requestAnimationFrame(update);
            }
            
            window.addEventListener('resize', () => {
                if (window.innerWidth < 768) {
                    requestAnimationFrame(update);
                }
            })

            return swiper;
        }

        const mainImages = section.querySelectorAll('[data-main-image]');
        const preloadImages = section.querySelectorAll('[data-preload-image]');

        const removePreloadImages = () => {
            setTimeout(() => {
                const preloadImages = section.querySelectorAll('[data-preload-image]');
                preloadImages.forEach(preloadImage => {
                    preloadImage.remove();
                })
            }, 500)
        }

        const updateMainImages = () => {
            section.classList.add('loaded');
            const newImages = section.querySelectorAll('[data-main-image]');
            newImages.forEach(newImage => {
                newImage.removeAttribute('data-main-image');
            })
            removePreloadImages();
        }

        preloadImages.forEach(preloadImage => {
            if (preloadImage.complete) {
                section.classList.add('loaded');
            } else {
                preloadImage.addEventListener('load', () => {
                    section.classList.add('loaded');
                })
                preloadImage.addEventListener('error', () => {
                    section.classList.add('loaded');
                })
            }
        })
        mainImages.forEach(mainImage => {
            if (mainImage.complete) {
                updateMainImages();
            } else {
                mainImage.addEventListener('load', () => {
                    updateMainImages();
                })
                mainImage.addEventListener('error', () => {
                    updateMainImages();
                })
            }
        })

        numSlides = swiperEl.querySelectorAll('.swiper-slide-hero').length;

        if (numSlides > 0) {

            const swiper = createSwiper(swiperEl);
            swiperEl.classList.add('loaded');
    
            const mobileSwiper = createMobileSwiper(mobileSwiperEl);
            mobileSwiperEl.classList.add('loaded');
    
            swiper.controller.control = mobileSwiper;
            mobileSwiper.controller.control = swiper;

            window.addEventListener('load', () => {
                gsap.fromTo(circleDecoration, 
                    { rotate: 350, yPercent: 0 },
                    { rotate: 0, duration: 1.5, yPercent: -50, ease: 'power4.out' }
                );
            })

            const goToNext = () => {
                slidingViaNavigation = "NEXT";

                const currentRealIndex = swiper.realIndex;
                const nextRealIndex = (currentRealIndex + 1) % numSlides;
                const duration = swiper.params.speed / 1000 || 0.45;

                // Prevent Swiper's built-in animation
                swiper.slideToLoop(nextRealIndex, duration * 1000);
                swiper.animating = true;
              
                // Use GSAP to reset animating flag after transition is done
                gsap.delayedCall(duration, () => {
                    swiper.animating = false;
                    slidingViaNavigation = null;
                });
            };

            const goToPrev = () => {
                slidingViaNavigation = "BACK";
            
                const currentRealIndex = swiper.realIndex;
                const prevRealIndex = (currentRealIndex - 1 + numSlides) % numSlides;
                const duration = swiper.params.speed / 1000 || 0.45;

                swiper.slideToLoop(prevRealIndex, duration * 1000);
                swiper.animating = true;
                
                gsap.delayedCall(duration, () => {
                    swiper.animating = false;
                    slidingViaNavigation = null;
                });
            };

            const arrowRight = section.querySelector('.circle-decoration .image-holder--arrow-right');
            const arrowLeft = section.querySelector('.circle-decoration .image-holder--arrow-left');
            arrowRight.addEventListener('click', goToNext);
            arrowLeft.addEventListener('click', goToPrev);
        }
        
        const mobileFeaturedProductsModals = section.querySelectorAll('.modal--mobile-featured-products');
        mobileFeaturedProductsModals.forEach((modal) => {
            const slidesCount = modal.querySelectorAll('.swiper-slide').length;
            const swiper = new Swiper(modal.querySelector('.swiper'), {
                slidesPerView: 1,
                spaceBetween: 32,
                loop: slidesCount > 1,
                watchOverflow: true,
                navigation: {
                    nextEl: modal.querySelector('.swiper-button-next'),
                    prevEl: modal.querySelector('.swiper-button-prev'),
                },
            })
            document.body.append(modal);
        })
    }
}